import React from 'react';
import {
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonButton,
} from '@ionic/react';
import { IssueFlaggerOutput } from '../parameters-schema';
import { useTags } from '../../tags/tags-provider';
import { TagType } from '../../tags/tags-schema';
import LeafEditorModal from '../shared/leaf-editor-modal';
import ReorderableTagSection from '../shared/reorderable-tag-section';
import SectionHeader from '../shared/section-header';
import SectionContainer from '../shared/section-container';
import { useLeafEditor } from '../shared/use-leaf-editor';

interface IssueFlaggerLeafEditorProps {
  output: IssueFlaggerOutput;
  onUpdate: (newOutput: IssueFlaggerOutput) => void;
  isOpen: boolean;
  onClose: () => void;
}

const IssueFlaggerLeafEditor: React.FC<IssueFlaggerLeafEditorProps> = ({
  output,
  onUpdate,
  isOpen,
  onClose
}) => {
  const { tags } = useTags();
  
  const {
    editingOutput,
    setEditingOutput,
    newTagName,
    setNewTagName,
    newTagDescription,
    setNewTagDescription,
    isAddingNewTag,
    handleSave,
    handleAddTag,
    handleCancelTagCreation,
    handleReorder,
    handleRemoveItem,
    handleSelectExisting,
  } = useLeafEditor({
    output,
    onUpdate,
    isOpen,
    onClose
  });

  const handleAddIssueTag = async () => {
    const newTag = await handleAddTag(false);
    if (newTag) {
      setEditingOutput(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag]
      }));
    }
  };

  return (
    <LeafEditorModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Output"
    >
      {/* Basic Information Section */}
      <SectionHeader
        title="Basic Information"
        subtitle="Core details for this inference rule"
        icon="information-circle"
      >
        <SectionContainer>
          <IonItem>
            <IonLabel position="stacked">Status</IonLabel>
            <IonSelect
              value={editingOutput.lightStatus || ''}
              onIonChange={e => setEditingOutput({
                ...editingOutput,
                lightStatus: e.detail.value || ''
              })}
            >
              <IonSelectOption value="">None</IonSelectOption>
              <IonSelectOption value="green">Green</IonSelectOption>
              <IonSelectOption value="yellow">Yellow</IonSelectOption>
              <IonSelectOption value="red">Red</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Explanation</IonLabel>
            <IonTextarea
              value={editingOutput.explanation}
              onIonChange={e => setEditingOutput({
                ...editingOutput,
                explanation: e.detail.value ?? ''
              })}
              placeholder="Enter an explanation"
            />
          </IonItem>
        </SectionContainer>
      </SectionHeader>

      {/* Issue Tags Section */}
      <SectionHeader
        title="Issue Tags"
        subtitle="Non-product tags for categorizing this inference rule"
        icon="pricetag"
      >
        <SectionContainer>
          <ReorderableTagSection
            items={(editingOutput.tags || []).map(tagId => {
              const tag = tags?.find((t: TagType) => t.id === tagId);
              return {
                id: tagId,
                displayName: tag?.displayName || tagId,
                description: tag?.description || 'No description'
              };
            })}
            onReorder={(from, to) => handleReorder('tags', from, to)}
            onRemove={(tagId) => handleRemoveItem('tags', tagId)}
            onSelectExisting={(value) => handleSelectExisting('tags', value)}
            isProduct={false}
            label="Add Issue Tag"
            placeholder="Select a tag or create new"
            emptyMessage="No issue tags"
            isAddingNewTag={isAddingNewTag}
            tagName={newTagName}
            tagDescription={newTagDescription}
            onTagNameChange={setNewTagName}
            onTagDescriptionChange={setNewTagDescription}
            onAddTag={handleAddIssueTag}
            onCancelTagCreation={handleCancelTagCreation}
            addingProduct={false}
          />
        </SectionContainer>
      </SectionHeader>

      <div className="button-group">
        <IonButton onClick={handleSave}>Save Changes</IonButton>
        <IonButton 
          fill="outline" 
          onClick={onClose}
        >
          Cancel
        </IonButton>
      </div>
    </LeafEditorModal>
  );
};

export default IssueFlaggerLeafEditor;
